import React from 'react'
import { ListItemButton, ListItemText, Typography, Box, IconButton, ListItem } from '@mui/material'
import { makeStyles } from '@mui/styles'
import FiberManualRecordIcon from '@mui/icons-material/FiberManualRecord'
import DeleteIcon from '@mui/icons-material/DeleteOutline'
import clsx from 'clsx'

import ThemeInterface from '@/theme-interface'
import { type Conversation } from '@/redux/ducks/ai/chat'
import { getTimestamp } from '@/modules/base/components/ai/_helpers/getTimestamp'

const useStyles = makeStyles((theme: ThemeInterface) => ({
  listItem: {
    borderBottom: theme.border,
    containerType: 'inline-size',
  },
  listItemButton: {
    padding: '12px 16px',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-start',
    '&.Mui-selected': {
      backgroundColor: theme.backgroundColorPrimaryLight,
      '&:hover': {
        backgroundColor: theme.backgroundColorPrimaryLight,
      },
    },
  },
  conversationTitle: {
    fontWeight: theme.fontWeights.medium,
    fontSize: theme.fontSizes.small,
    marginBottom: 4,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    width: '100%',
    maxWidth: 'calc(100cqw - 90px)',
    flex: 1,
    paddingRight: 8,
  },
  conversationTitleUnread: {
    fontWeight: theme.fontWeights.bold,
  },
  unreadIndicator: {
    display: 'flex',
    alignItems: 'center',
    width: '100%',
  },
  unreadDot: {
    fontSize: 10,
  },
  conversationTime: {
    fontSize: '0.7rem',
    color: theme.textMuted,
    marginTop: 4,
  },
}))

interface AiChatConversationListItemProps {
  conversation: Conversation
  isSelected: boolean
  isUnread: boolean
  onSelect: (conversationId: string) => void
  onDelete: (conversationId: string) => void
}

export default function AiChatConversationListItem(props: AiChatConversationListItemProps) {
  const { conversation, isSelected, isUnread, onSelect, onDelete } = props
  const classes = useStyles()

  return (
    <ListItem
      key={conversation.id}
      className={classes.listItem}
      disablePadding
      secondaryAction={
        <IconButton size="small" onClick={() => onDelete(conversation.id)}>
          <DeleteIcon fontSize="small" />
        </IconButton>
      }
    >
      <ListItemButton
        selected={isSelected}
        onClick={() => onSelect(conversation.id)}
        className={classes.listItemButton}
      >
        <ListItemText
          primary={
            <Box className={classes.unreadIndicator}>
              <Typography className={clsx(classes.conversationTitle, { [classes.conversationTitleUnread]: isUnread })}>
                {conversation.title}
              </Typography>
              {isUnread && <FiberManualRecordIcon color="error" className={classes.unreadDot} />}
            </Box>
          }
          secondary={
            <Typography className={classes.conversationTime}>
              {getTimestamp(conversation.createdAt)}
            </Typography>
          }
        />
      </ListItemButton>
    </ListItem>
  )
}
